import { getBackendUrl } from "@/utils/api";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Camera, 
  Video, 
  Edit3, 
  Settings, 
  Layout,
  Play,
  Share2,
  BarChart3,
  Sliders,
  RefreshCw,
  Eye,
  MessageSquare,
  Phone,
  ArrowRight,
  ArrowDown,
  CheckCircle2
} from "lucide-react";
import { Button } from "../components/ui/button";
import PageShell from "../components/PageShell";
import { useSiteStyles } from "../hooks/useSiteStyles";
import axios from "axios";

const API = getBackendUrl();

const HowItWorksPage = () => {
  const { styles } = useSiteStyles();
  const [pageContent, setPageContent] = useState(null);

  useEffect(() => {
    fetchPageContent();
  }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${API}/api/page-content/howitworks`);
      setPageContent(response.data);
    } catch (error) {
      console.error("Failed to fetch page content:", error);
    }
  };

  // Default content (used if CMS content not available)
  // Use CMS content or defaults
  const heroTitle = pageContent?.hiw_hero_title || "How It Works";
  const heroSubtitle = pageContent?.hiw_hero_subtitle || "Our Process";
  const heroDescription = pageContent?.hiw_hero_description || "A comprehensive approach to marketing your property online. From initial setup to ongoing campaign management, we handle everything.";
  
  const phase1Title = pageContent?.hiw_phase1_title || "Preparation";
  const phase1Subtitle = pageContent?.hiw_phase1_subtitle || "Getting everything ready for your campaign";
  
  const phase2Title = pageContent?.hiw_phase2_title || "Launching & Running Campaign";
  const phase2Subtitle = pageContent?.hiw_phase2_subtitle || "Active campaign management cycle";
  
  const phase3Title = pageContent?.hiw_phase3_title || "Monitor Engagement";
  const phase3Subtitle = pageContent?.hiw_phase3_subtitle || "Track your campaign performance";
  
  const ctaTitle = pageContent?.hiw_cta_title || "Ready to Get Started?";
  const ctaDescription = pageContent?.hiw_cta_description || "Let us help you create a powerful online presence for your property. Our team will guide you through every step.";

  // Build steps from CMS content with fallbacks
  const preparationSteps = [
    { icon: Camera, title: pageContent?.hiw_prep_step1_title || "Capture Images", description: pageContent?.hiw_prep_step1_desc || "Professional photography of your property" },
    { icon: Video, title: pageContent?.hiw_prep_step2_title || "Capture Video", description: pageContent?.hiw_prep_step2_desc || "High-quality video walkthrough" },
    { icon: Edit3, title: pageContent?.hiw_prep_step3_title || "Edit Assets", description: pageContent?.hiw_prep_step3_desc || "Produce campaign stills and video" },
    { icon: Settings, title: pageContent?.hiw_prep_step4_title || "Setup Account", description: pageContent?.hiw_prep_step4_desc || "Configure your campaign platform" },
    { icon: Layout, title: pageContent?.hiw_prep_step5_title || "Create Landing Page", description: pageContent?.hiw_prep_step5_desc || "Build your property showcase page" }
  ];

  const launchSteps = [
    { icon: Play, title: pageContent?.hiw_launch_step1_title || "Begin Campaign", description: pageContent?.hiw_launch_step1_desc || "Launch your marketing campaign" },
    { icon: Share2, title: pageContent?.hiw_launch_step2_title || "Push to Social Media", description: pageContent?.hiw_launch_step2_desc || "Distribute video across platforms" },
    { icon: BarChart3, title: pageContent?.hiw_launch_step3_title || "Monitor Statistics", description: pageContent?.hiw_launch_step3_desc || "Track performance metrics" },
    { icon: Sliders, title: pageContent?.hiw_launch_step4_title || "Optimisation", description: pageContent?.hiw_launch_step4_desc || "Fine-tune for better results" },
    { icon: RefreshCw, title: pageContent?.hiw_launch_step5_title || "Repeat Campaign", description: pageContent?.hiw_launch_step5_desc || "Continuous improvement cycle" }
  ];

  const monitorSteps = [
    { icon: Eye, title: pageContent?.hiw_monitor_step1_title || "Page Visits", description: pageContent?.hiw_monitor_step1_desc || "Track visitor engagement" },
    { icon: MessageSquare, title: pageContent?.hiw_monitor_step2_title || "Enquiries", description: pageContent?.hiw_monitor_step2_desc || "Monitor lead generation" },
    { icon: Phone, title: pageContent?.hiw_monitor_step3_title || "Calls", description: pageContent?.hiw_monitor_step3_desc || "Track direct responses" }
  ];

  return (
    <PageShell>
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      {/* Navbar */}
      

      {/* Hero Section */}
      <section className="pt-12 md:pt-16 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p 
            className="text-sm font-medium tracking-widest uppercase mb-4"
            style={{ color: styles?.primary_color }}
          >
            {heroSubtitle}
          </p>
          <h1 className="font-serif text-4xl md:text-5xl font-medium mb-6">
            {heroTitle}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {heroDescription}
          </p>
        </div>
      </section>

      {/* Flowchart Section */}
      <section className="pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Phase 1: Preparation */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-8">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: styles?.primary_color || '#4a7c59' }}
              >
                1
              </div>
              <div>
                <h2 className="font-serif text-2xl font-medium">{phase1Title}</h2>
                <p className="text-muted-foreground">{phase1Subtitle}</p>
              </div>
            </div>
            
            <div className="relative">
              <div 
                className="absolute top-1/2 left-0 right-0 h-1 -translate-y-1/2 hidden lg:block"
                style={{ backgroundColor: styles?.primary_color ? `${styles.primary_color}20` : '#4a7c5920' }}
              />
              
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
                {preparationSteps.map((step, index) => (
                  <div key={index} className="relative">
                    <div className="bg-white border rounded-lg p-6 text-center hover:shadow-lg transition-shadow relative z-10">
                      <div 
                        className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: styles?.primary_color ? `${styles.primary_color}15` : '#4a7c5915' }}
                      >
                        <step.icon 
                          className="w-7 h-7" 
                          style={{ color: styles?.primary_color || '#4a7c59' }}
                        />
                      </div>
                      <h3 className="font-medium mb-2">{step.title}</h3>
                      <p className="text-sm text-muted-foreground">{step.description}</p>
                    </div>
                    {index < preparationSteps.length - 1 && (
                      <ArrowRight 
                        className="absolute top-1/2 -right-3 -translate-y-1/2 w-6 h-6 hidden lg:block z-20"
                        style={{ color: styles?.primary_color || '#4a7c59' }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Arrow Down */}
          <div className="flex justify-center mb-8">
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ backgroundColor: styles?.primary_color ? `${styles.primary_color}15` : '#4a7c5915' }}
            >
              <ArrowDown 
                className="w-6 h-6"
                style={{ color: styles?.primary_color || '#4a7c59' }}
              />
            </div>
          </div>

          {/* Phase 2: Launching & Running */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-8">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: styles?.accent_color || '#c9a55c' }}
              >
                2
              </div>
              <div>
                <h2 className="font-serif text-2xl font-medium">{phase2Title}</h2>
                <p className="text-muted-foreground">{phase2Subtitle}</p>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-r from-transparent via-amber-100 to-transparent p-8 rounded-2xl">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
                  {launchSteps.map((step, index) => (
                    <div key={index} className="relative">
                      <div className="bg-white border rounded-lg p-6 text-center hover:shadow-lg transition-shadow">
                        <div 
                          className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                          style={{ backgroundColor: styles?.accent_color ? `${styles.accent_color}20` : '#c9a55c20' }}
                        >
                          <step.icon 
                            className="w-7 h-7" 
                            style={{ color: styles?.accent_color || '#c9a55c' }}
                          />
                        </div>
                        <h3 className="font-medium mb-2">{step.title}</h3>
                        <p className="text-sm text-muted-foreground">{step.description}</p>
                      </div>
                      {index < launchSteps.length - 1 && (
                        <ArrowRight 
                          className="absolute top-1/2 -right-3 -translate-y-1/2 w-6 h-6 hidden lg:block z-10"
                          style={{ color: styles?.accent_color || '#c9a55c' }}
                        />
                      )}
                    </div>
                  ))}
                </div>
                
                <div className="flex justify-center mt-6">
                  <div 
                    className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
                    style={{ 
                      backgroundColor: styles?.accent_color ? `${styles.accent_color}20` : '#c9a55c20',
                      color: styles?.accent_color || '#c9a55c'
                    }}
                  >
                    <RefreshCw className="w-4 h-4" />
                    Continuous Improvement Cycle
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Arrow Down */}
          <div className="flex justify-center mb-8">
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ backgroundColor: styles?.primary_color ? `${styles.primary_color}15` : '#4a7c5915' }}
            >
              <ArrowDown 
                className="w-6 h-6"
                style={{ color: styles?.primary_color || '#4a7c59' }}
              />
            </div>
          </div>

          {/* Phase 3: Monitor Engagement */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-8">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: '#3b82f6' }}
              >
                3
              </div>
              <div>
                <h2 className="font-serif text-2xl font-medium">{phase3Title}</h2>
                <p className="text-muted-foreground">{phase3Subtitle}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              {monitorSteps.map((step, index) => (
                <div key={index} className="bg-white border rounded-lg p-8 text-center hover:shadow-lg transition-shadow">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: '#3b82f620' }}
                  >
                    <step.icon className="w-8 h-8 text-blue-500" />
                  </div>
                  <h3 className="font-medium text-lg mb-2">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div 
            className="rounded-2xl p-12 text-center text-white"
            style={{ backgroundColor: styles?.primary_color || '#4a7c59' }}
          >
            <CheckCircle2 className="w-16 h-16 mx-auto mb-6 opacity-80" />
            <h2 className="font-serif text-3xl font-medium mb-4">{ctaTitle}</h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              {ctaDescription}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/prices">
                <Button 
                  variant="outline" 
                  className="rounded-none bg-white hover:bg-white/90 border-white"
                  style={{ color: styles?.primary_color || '#4a7c59' }}
                >
                  View Pricing
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  className="rounded-none bg-white/20 hover:bg-white/30 border border-white/40"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      </div>
    </PageShell>
  );
};

export default HowItWorksPage;
